import React, { useEffect, useState } from "react";
import { Form, Row } from "reactstrap";
import { Card, CardBody, CardHeader, Col } from "react-bootstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import ControlledSelectForFiltering from "../../components/ContolledSelectForFiltering";
import ViewButtonComponent from "../../components/ViewButtonComponent";
import DeleteButtonComponent from "../../components/DeleteButtonComponent";
import BreadCrumbs from "../../components/BreadCrumbs";
import ButtonComponentV2 from "../../components/ButtonComponentV2";
// import ControlledTextField from "../../components/ControlledTextField";
import TableLoadingComponent from "../../components/TableLoadingComponent";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import man from "../../assets/icons/man.PNG";
import { useLazyGetAdminsQuery, useDeleteAdminMutation, Role } from "../../redux/services/adminService";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { ADMIN_STATUS } from "../../constants/constans";
import StatusBadge from "../../components/StatusBatch";
import RoleBadge from "../../components/RoleBadge";
import Select from "react-select";
import { Label } from "reactstrap";

export interface AdminData {
    id: string;
    userId: string;
    roles: Role[];
    displayName: string;
    email: string;
    phoneNumber?: string;
    photoURL?: string;
    status: string;
    createdAt: {
        _seconds: number;
        _nanoseconds: number;
    };
    updatedAt?: {
        _seconds: number;
        _nanoseconds: number;
    };
}

export type AdminFilterData = {
    adminStatus: { value: string; label: string };
    searchAdmin: string;
};

interface AdminSearchOption {
    value: string;
    label: string;
    adminId: string;
}

const Admins: React.FC = () => {
    const navigate = useNavigate();
    const [initialList, setInitialList] = useState<AdminData[]>([]);
    const [adminList, setAdminList] = useState<AdminData[]>([]);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [deletingAdminId, setDeletingAdminId] = useState<string | null>(null);
    
    // Search dropdown state
    const [searchOptions, setSearchOptions] = useState<AdminSearchOption[]>([]);
    const [selectedSearchOption, setSelectedSearchOption] = useState<AdminSearchOption | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [isSearching, setIsSearching] = useState(false);

    // API hooks
    const [getAdmins, { isLoading: isFetchingAdmins }] = useLazyGetAdminsQuery();
    const [deleteAdmin] = useDeleteAdminMutation();

    const { control, handleSubmit, watch } = useForm<AdminFilterData>({
        defaultValues: {
            adminStatus: { value: "All", label: "All" },
            searchAdmin: "",
        },
    });

    const adminStatusWatcher = watch("adminStatus");
    const isLoading = isFetchingAdmins;

    // Fetch admins from API
    const fetchAdmins = async () => {
        try {
            const result = await getAdmins({}).unwrap();
            const normalizedAdmins = Array.isArray(result) ? result : result.admins || [];
            setAdminList(normalizedAdmins);
            setInitialList(normalizedAdmins);
            
            // Generate search options when data is loaded
            generateSearchOptions(normalizedAdmins);
        } catch (error) {
            console.error('Error fetching admins:', error);
            toast.error('Failed to fetch admin users');
        }
    };

    // Generate search options from admin display names only (limited to 4 suggestions)
    const generateSearchOptions = (admins: AdminData[]) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const options: AdminSearchOption[] = [];
        const addedNames = new Set<string>();

        // First collect all unique names
        const allUniqueNames: AdminSearchOption[] = [];
        admins.forEach(admin => {
            if (admin.displayName && !addedNames.has(admin.displayName.toLowerCase())) {
                allUniqueNames.push({
                    value: admin.displayName,
                    label: admin.displayName,
                    adminId: admin.id
                });
                addedNames.add(admin.displayName.toLowerCase());
            }
        });

        // Sort alphabetically first, then take first 4
        const sortedOptions = allUniqueNames
            .sort((a, b) => a.label.localeCompare(b.label))
            .slice(0, 4);

        setSearchOptions(sortedOptions);
        // No default selection, let user choose
        setSelectedSearchOption(null);
    };

    // Handle search option selection
    const handleSearchSelect = (selectedOption: AdminSearchOption | null) => {
        setSelectedSearchOption(selectedOption);
        applyFilters(selectedOption, adminStatusWatcher);
    };

    // Handle search input change (for typing in the select) - filter and limit to 4
    const handleSearchInputChange = (inputValue: string) => {
        if (!inputValue || inputValue.length === 0) {
            // Reset to first 4 options when input is cleared
            generateSearchOptions(initialList);
            return;
        }

        // Filter existing options based on input and limit to 4
        const allMatchingOptions = initialList
            .filter(admin => 
                admin.displayName && 
                admin.displayName.toLowerCase().includes(inputValue.toLowerCase())
            )
            .map(admin => ({
                value: admin.displayName,
                label: admin.displayName,
                adminId: admin.id
            }))
            .slice(0, 4); // Limit to 4 suggestions

        // Remove duplicates
        const uniqueOptions = allMatchingOptions.filter((option, index, array) => 
            array.findIndex(item => item.label.toLowerCase() === option.label.toLowerCase()) === index
        );

        setSearchOptions(uniqueOptions);
    };

    // Simplified client-side filtering function
    const applyFilters = (searchOption?: AdminSearchOption | null, statusOption?: { value: string; label: string }) => {
        let filteredData = [...initialList];

        // Apply search filter based on selected display name
        const currentSearchOption = searchOption !== undefined ? searchOption : selectedSearchOption;
        if (currentSearchOption) {
            const searchValue = currentSearchOption.value.toLowerCase();
            filteredData = filteredData.filter(admin =>
                admin.displayName?.toLowerCase().includes(searchValue)
            );
        }

        // Apply status filter
        const currentStatusOption = statusOption !== undefined ? statusOption : adminStatusWatcher;
        if (currentStatusOption?.value && currentStatusOption.value !== "All") {
            filteredData = filteredData.filter(admin =>
                admin.status === currentStatusOption.value
            );
        }

        setAdminList(filteredData);
    };

    // Simple option component without icons
    const formatSearchOption = (option: AdminSearchOption) => {
        return (
            <div className="d-flex align-items-center">
                <div>{option.label}</div>
            </div>
        );
    };

    /* 
    // Commented algolia search implementation
    const handleSearchChange = (searchTerm: string) => {
        if (suggestionTimeoutRef.current) {
            clearTimeout(suggestionTimeoutRef.current);
        }

        suggestionTimeoutRef.current = setTimeout(() => {
            const suggestions = generateSearchSuggestions(searchTerm);
            setSearchSuggestions(suggestions);
            setShowSuggestions(suggestions.length > 0 && searchTerm.length >= 2);
            setActiveSuggestionIndex(-1);
        }, 300);
    };

    const handleSuggestionClick = (suggestion: SearchSuggestion) => {
        setValue("searchAdmin", suggestion.value);
        setShowSuggestions(false);
        setSearchSuggestions([]);
        setActiveSuggestionIndex(-1);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!showSuggestions || searchSuggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveSuggestionIndex(prev => 
                    prev < searchSuggestions.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveSuggestionIndex(prev => 
                    prev > 0 ? prev - 1 : searchSuggestions.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (activeSuggestionIndex >= 0 && searchSuggestions[activeSuggestionIndex]) {
                    handleSuggestionClick(searchSuggestions[activeSuggestionIndex]);
                }
                break;
            case 'Escape':
                setShowSuggestions(false);
                setActiveSuggestionIndex(-1);
                break;
        }
    };
    */

    const handleChangeRowsPerPage = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
    };

    const handleViewAdminDetails = (admin: AdminData) => {
        navigate("/admins/admins-list/view", { state: { admin } });
    };

    const handleDeleteAdmin = async (admin: AdminData) => {
        Swal.fire({
            title: "Are you sure?",
            text: `You are about to delete admin "${admin.displayName}". This action cannot be undone!`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#590B21",
            cancelButtonColor: "#ddd",
            confirmButtonText: "Yes, delete it!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                setDeletingAdminId(admin.id);
                try {
                    await deleteAdmin({ id: admin.id }).unwrap();

                    const updatedAdminList = adminList.filter(adminItem => adminItem.id !== admin.id);
                    const updatedInitialList = initialList.filter(adminItem => adminItem.id !== admin.id);

                    setAdminList(updatedAdminList);
                    setInitialList(updatedInitialList);
                    
                    // Regenerate search options with updated data
                    generateSearchOptions(updatedInitialList);

                    Swal.fire("Deleted!", "Admin user has been deleted successfully.", "success");
                    setDeletingAdminId(null);
                } catch (error) {
                    console.error('Error deleting admin:', error);
                    Swal.fire("Error!", "An error occurred while deleting the admin user.", "error");
                    setDeletingAdminId(null);
                }
            }
        });
    };

    const handleCreateAdmin = () => {
        navigate("/admins/createAdmin");
    };

    // Apply filters whenever status changes or when search option changes
    useEffect(() => {
        if (initialList.length > 0) {
            applyFilters();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminStatusWatcher, initialList]);

    useEffect(() => {
        fetchAdmins();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSubmit = async (formData: AdminFilterData) => { };

    const columns: TableColumn<AdminData>[] = [
        {
            name: <h6 className="fw-bold">Photo</h6>,
            selector: (row) => row.photoURL || "",
            cell: (row) => (
                <img
                    src={row.photoURL || man}
                    alt={row.displayName}
                    style={{
                        width: "50px",
                        height: "50px",
                        borderRadius: "10px",
                        margin: "5px",
                    }}
                    onError={(e) => {
                        e.currentTarget.src = man;
                    }}
                />
            ),
            sortable: false,
        },
        {
            name: <h6 className="fw-bold">Name</h6>,
            cell: (row) => (
                <div style={{ fontSize: "14px", fontWeight: "bold" }}>
                    <div>{row.displayName}</div>
                    <small className="text-muted">{row.email || 'N/A'}</small>
                </div>
            ),
            sortable: true,
        },
        {
            name: <h6 className="fw-bold">Role</h6>,
            cell: (row) => <RoleBadge roles={row.roles || []} variant="secondary" />,
            sortable: true,
        },
        {
            name: <h6 className="fw-bold">Status</h6>,
            cell: (row) => <StatusBadge status={row.status} />,
            sortable: true,
        },
        {
            name: <h6 className="fw-bold">Created Date</h6>,
            cell: (row) => {
                const createdDate = row.createdAt?._seconds
                    ? new Date(row.createdAt._seconds * 1000).toLocaleDateString()
                    : 'N/A';
                return <span>{createdDate}</span>;
            },
            sortable: true,
        },
        {
            name: <h6 className="fw-bold">Actions</h6>,
            cell: (row) => (
                <div className="d-flex gap-2">
                    <ViewButtonComponent onClick={() => handleViewAdminDetails(row)} />
                    <DeleteButtonComponent
                        onClick={() => handleDeleteAdmin(row)}
                        disabled={deletingAdminId === row.id}
                        loading={deletingAdminId === row.id}
                    />
                </div>
            ),
            sortable: false,
            center: true,
        },
    ];

    return (
        <>
            <BreadCrumbs hasSubMenus={false} />
            <div>
                <Form
                    className="auth-login-form mt-2"
                    onSubmit={handleSubmit(onSubmit)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                        }
                    }}
                >
                    <Row className="cart-search-top-details-whole-component d-flex justify-content-between align-items-end">
                        <Col lg="3" xs="12" className="mb-2 mb-lg-0">
                            <h4>Admin Users</h4>
                        </Col>
                        <Col
                            lg="9"
                            sm="12"
                            className="d-flex justify-content-end align-items-end"
                        >
                            <div className="d-flex align-items-end justify-content-between gap-3 flex-wrap-end">
                                {/* Search with Display Names Only - 4 suggestions max */}
                                <div className="d-flex flex-column px-1">
                                    <Label>Search Admin</Label>
                                    <Select
                                        isMulti={false}
                                        name="searchAdmin"
                                        value={selectedSearchOption}
                                        isSearchable={true}
                                        isClearable={true}
                                        isLoading={isSearching}
                                        onInputChange={handleSearchInputChange}
                                        onChange={handleSearchSelect}
                                        options={searchOptions}
                                        placeholder="Search admin name..."
                                        formatOptionLabel={formatSearchOption}
                                        className="custom-input"
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minWidth: '250px'
                                            }),
                                            menu: (base) => ({
                                                ...base,
                                                maxHeight: '120px'
                                            }),
                                            menuList: (base) => ({
                                                ...base,
                                                maxHeight: '120px',
                                                overflowY: 'auto'
                                            })
                                        }}
                                        maxMenuHeight={120}
                                    />
                                </div>

                                <div className="cartStatus-dropDown-component">
                                    <ControlledSelectForFiltering
                                        label={"Admin Status"}
                                        inputName={"adminStatus"}
                                        control={control}
                                        optionsList={ADMIN_STATUS}
                                        className="custom-input"
                                    />
                                </div>

                                <div>
                                    <ButtonComponentV2
                                        titel="Add Admin"
                                        icon={<FontAwesomeIcon icon={faPlus} />}
                                        onClick={handleCreateAdmin}
                                        color="#fff"
                                        backgroundColor="#590B21"
                                    />
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Card>
                        <CardHeader className="card-header">
                            <div className="d-flex justify-content-between p-2">
                                <h5 className="">
                                    Admin Users Management
                                    {adminList.length !== initialList.length && (
                                        <small className="text-muted ms-2">
                                            ({adminList.length} of {initialList.length} admins)
                                        </small>
                                    )}
                                </h5>
                            </div>
                            <hr />
                        </CardHeader>
                        <CardBody>
                            {isLoading ? (
                                <TableLoadingComponent numColumns={6} />
                            ) : (
                                <DataTable
                                    columns={columns}
                                    data={adminList}
                                    responsive
                                    pagination
                                    highlightOnHover
                                    pointerOnHover
                                    paginationPerPage={rowsPerPage}
                                    onChangeRowsPerPage={handleChangeRowsPerPage}
                                    noDataComponent={
                                        <div style={{ padding: '20px', textAlign: 'center' }}>
                                            {selectedSearchOption || adminStatusWatcher?.value !== "All" ? 
                                                "No admin users match your search criteria" : 
                                                "No admin users found"
                                            }
                                        </div>
                                    }
                                    fixedHeader
                                    fixedHeaderScrollHeight="600px"
                                />
                            )}
                        </CardBody>
                    </Card>
                </Form>
            </div>
        </>
    );
};

export default Admins;